/**
 * Offline outbox for check-ins.
 * Every check-in is written to IndexedDB first, shown immediately, and delivered to the server
 * when possible (now, when the connection returns, when the app becomes visible, every 30 s).
 * The server applies "last write wins" by clientTs, so re-sending is always safe.
 */
import { occurrenceKey, type Checkin } from '@planer/shared';
import { createStore, del, entries, set, type UseStore } from 'idb-keyval';
import { useSyncExternalStore } from 'react';
import { api, ApiError, NetworkError } from './api';

let store: UseStore | undefined;
try {
  store = typeof indexedDB === 'undefined' ? undefined : createStore('planer-outbox', 'checkins');
} catch {
  store = undefined; // e.g. private mode without IndexedDB: the outbox still works in memory
}

let pending: ReadonlyMap<string, Checkin> = new Map();
const listeners = new Set<() => void>();
const emit = () => {
  listeners.forEach((fn) => {
    fn();
  });
};

function replace(next: Map<string, Checkin>) {
  pending = next;
  emit();
}

async function persist(action: () => Promise<void>) {
  if (!store) return;
  try {
    await action();
  } catch (err) {
    console.warn('outbox: IndexedDB write failed', err);
  }
}

export async function loadOutbox(): Promise<void> {
  if (!store) return;
  try {
    const rows = await entries<string, Checkin>(store);
    if (rows.length > 0) replace(new Map([...rows, ...pending]));
  } catch (err) {
    console.warn('outbox: could not read IndexedDB', err);
  }
}

export async function enqueueCheckin(checkin: Checkin): Promise<void> {
  const key = occurrenceKey(checkin.itemId, checkin.date);
  replace(new Map(pending).set(key, checkin));
  await persist(() => set(key, checkin, store));
  void flushOutbox();
}

export type FlushResult = 'empty' | 'ok' | 'offline' | 'error';
let inFlight: Promise<FlushResult> | null = null;
let onSynced: ((applied: Checkin[]) => void) | undefined;

export function flushOutbox(): Promise<FlushResult> {
  inFlight ??= doFlush().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doFlush(): Promise<FlushResult> {
  let sentAny = false;
  // Loop (not recursion): changes made while a batch was in flight are sent in the next round.
  for (;;) {
    const batch = [...pending.values()].slice(0, 500);
    if (batch.length === 0) return sentAny ? 'ok' : 'empty';
    // `onLine` is undefined outside browsers (tests); only an explicit false means offline.
    if ((navigator as { onLine?: boolean }).onLine === false) return 'offline';
    let delivered = true;
    try {
      await api.putCheckins(batch);
    } catch (err) {
      if (err instanceof NetworkError) return 'offline';
      // 400 = invalid data that will never succeed: drop it instead of retrying forever.
      if (!(err instanceof ApiError && err.status === 400)) return 'error';
      console.error('outbox: server rejected check-ins', err.body);
      delivered = false;
    }
    // Hand the delivered state to the query cache *before* dropping it from pending, so the UI
    // never falls back to stale server data for a moment.
    if (delivered) onSynced?.(batch);
    // Remove only what was sent and has not been changed again in the meantime.
    const next = new Map(pending);
    for (const c of batch) {
      const key = occurrenceKey(c.itemId, c.date);
      if (next.get(key)?.clientTs === c.clientTs) {
        next.delete(key);
        await persist(() => del(key, store));
      }
    }
    replace(next);
    sentAny = true;
    if (next.size === 0) return 'ok';
  }
}

/** Starts background delivery; returns a cleanup function. */
export function startOutboxSync(synced: (applied: Checkin[]) => void): () => void {
  onSynced = synced;
  const trigger = () => void flushOutbox();
  const onVisible = () => {
    if (document.visibilityState === 'visible') trigger();
  };
  window.addEventListener('online', trigger);
  document.addEventListener('visibilitychange', onVisible);
  const timer = window.setInterval(() => {
    if (pending.size > 0) trigger();
  }, 30_000);
  void loadOutbox().then(trigger);
  return () => {
    window.removeEventListener('online', trigger);
    document.removeEventListener('visibilitychange', onVisible);
    window.clearInterval(timer);
  };
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

/** Test helper: resets in-memory state. */
export function __resetOutboxForTests(): void {
  pending = new Map();
  inFlight = null;
  onSynced = undefined;
}

export function pendingCount(): number {
  return pending.size;
}

export function useOutbox(): ReadonlyMap<string, Checkin> {
  return useSyncExternalStore(subscribe, () => pending);
}
